/**
 * Adaptive System for Accessory Exercises
 * 
 * Unlike main lifts (which use %1RM), accessory exercises adapt based on:
 * 1. Past performance (last weight/reps/RPE)
 * 2. RPE feedback (if RPE was too high/low, adjust)
 * 3. Progressive overload (gradually increase over time)
 * 4. Volume accumulation (track total volume trends)
 */

import { getSessionHistory, type SessionLog } from './sessionStorage';
import { computeSetVolume } from './volumeTracking';
import { roundToNearestPlate } from './plateMath';
import { getCanonical1RMKey } from './oneRM';

export interface AccessoryRecommendation {
  suggestedWeight: number;
  suggestedReps: number;
  confidence: 'high' | 'medium' | 'low';
  reasoning: string;
  dataPoints?: number;  // Number of historical sessions used for this recommendation
  lastPerformance?: {
    weight: number;
    reps: number;
    rpe: number;
    date: string;
  };
}

/**
 * HYBRID APPROACH: Combine periodization with adaptive feedback
 *
 * Priority:
 * 1. Use periodization-calculated weight as the foundation (if available)
 * 2. Fall back to past performance if no periodization weight (no 1RM)
 * 3. Apply adaptive adjustments based on RPE feedback
 * 4. Respect readiness/recovery multipliers
 *
 * This ensures progressive overload follows the program while preventing injury
 */
export async function getHybridRecommendation(
  exerciseName: string,
  targetReps: number,
  periodizationWeight: number, // Weight calculated from 1RM + phase intensity (can be 0 if no 1RM)
  readinessMultiplier: number = 1.0 // From recovery/readiness assessment
): Promise<AccessoryRecommendation> {
  const sessions = await getSessionHistory();
  const pastPerformances = findPastPerformances(exerciseName, sessions, 3);

  // CASE 1: No periodization weight (no 1RM) - use pure adaptive approach
  if (periodizationWeight === 0 || !periodizationWeight) {
    if (pastPerformances.length === 0) {
      // No history - use estimated starting weight
      const startingWeight = estimateStartingWeight(exerciseName);
      return {
        suggestedWeight: roundToNearestPlate(startingWeight * readinessMultiplier),
        suggestedReps: targetReps,
        confidence: 'low',
        reasoning: 'First time - starting with baseline weight',
        dataPoints: 0,
      };
    }

    // Use past performance for progression (RPE-based)
    const lastPerformance = pastPerformances[0];
    const recommendation = calculateProgression(lastPerformance, pastPerformances, targetReps);
    const adjustedWeight = recommendation.suggestedWeight * readinessMultiplier;

    return {
      suggestedWeight: roundToNearestPlate(adjustedWeight),
      suggestedReps: recommendation.suggestedReps,
      confidence: recommendation.confidence,
      reasoning: recommendation.reasoning,
      dataPoints: pastPerformances.length,
      lastPerformance: {
        weight: lastPerformance.weight,
        reps: lastPerformance.reps,
        rpe: lastPerformance.rpe,
        date: lastPerformance.date,
      },
    };
  }

  // CASE 2: No history - use periodization weight with readiness adjustment
  if (pastPerformances.length === 0) {
    const adjustedWeight = periodizationWeight * readinessMultiplier;
    return {
      suggestedWeight: roundToNearestPlate(adjustedWeight),
      suggestedReps: targetReps,
      confidence: 'medium',
      reasoning: `Periodization: ${Math.round(periodizationWeight)} lbs (adjusted for readiness)`,
      dataPoints: 0,
    };
  }
  
  const lastPerformance = pastPerformances[0];
  
  // Calculate average RPE from recent performances for trend analysis
  const avgRPE = pastPerformances.reduce((sum, p) => sum + p.rpe, 0) / pastPerformances.length;
  const rpeConsistency = calculateRPEConsistency(pastPerformances);
  
  // Use average RPE for more stable recommendations, but weight recent performance more
  // 60% weight on last session, 40% on historical average
  const effectiveRPE = (lastPerformance.rpe * 0.6) + (avgRPE * 0.4);
  
  // Calculate adaptive adjustment multiplier based on RPE trend
  let adaptiveMultiplier = 1.0;
  let reasoning = '';
  
  // IMPROVED: Use trend-based RPE with consistency weighting
  if (effectiveRPE >= 9) {
    // Too hard - reduce by 7%
    adaptiveMultiplier = 0.93;
    reasoning = `RPE trend ${effectiveRPE.toFixed(1)} (last: ${lastPerformance.rpe}) too high - reducing 7%`;
  } else if (effectiveRPE <= 5) {
    // Way too easy - increase by 10%
    adaptiveMultiplier = 1.10;
    reasoning = `RPE trend ${effectiveRPE.toFixed(1)} (last: ${lastPerformance.rpe}) very low - increasing 10%`;
  } else if (effectiveRPE <= 6) {
    // Too easy - increase by 7.5%
    adaptiveMultiplier = 1.075;
    reasoning = `RPE trend ${effectiveRPE.toFixed(1)} (last: ${lastPerformance.rpe}) low - increasing 7.5%`;
  } else if (effectiveRPE <= 7) {
    // Slightly easy - increase by 5%
    adaptiveMultiplier = 1.05;
    reasoning = `RPE trend ${effectiveRPE.toFixed(1)} (last: ${lastPerformance.rpe}) - standard 5% progression`;
  } else if (effectiveRPE <= 8) {
    // Perfect RPE - small increase
    adaptiveMultiplier = 1.025;
    reasoning = `RPE trend ${effectiveRPE.toFixed(1)} (last: ${lastPerformance.rpe}) in target - small 2.5% progression`;
  }
  
  // Boost confidence if RPE is consistent across sessions
  const confidenceBoost = rpeConsistency > 0.8 ? ' (high confidence)' : rpeConsistency > 0.6 ? ' (moderate confidence)' : ' (variable RPE)';
  reasoning += confidenceBoost;
  
  // Check if periodization is asking for a big jump
  const percentChange = ((periodizationWeight - lastPerformance.weight) / lastPerformance.weight) * 100;
  
  if (percentChange > 15) {
    // Cap jumps at 10% for safety
    const cappedWeight = lastPerformance.weight * 1.10;
    return {
      suggestedWeight: roundToNearestPlate(cappedWeight * readinessMultiplier),
      suggestedReps: targetReps,
      confidence: 'high',
      reasoning: `Capped jump to 10% for safety (periodization wanted ${Math.round(percentChange)}%)`,
      dataPoints: pastPerformances.length,
      lastPerformance: {
        weight: lastPerformance.weight,
        reps: lastPerformance.reps,
        rpe: lastPerformance.rpe,
        date: lastPerformance.date,
      },
    };
  }
  
  // Apply hybrid calculation: periodization × adaptive × readiness
  let finalWeight = periodizationWeight * adaptiveMultiplier * readinessMultiplier;
  
  // CRITICAL FIX: If periodization weight equals last weight (HST session-to-session issue),
  // and RPE trend shows capacity, push forward based on last performance instead
  if (Math.abs(periodizationWeight - lastPerformance.weight) < 5 && effectiveRPE <= 7.5) {
    // Periodization isn't progressing, but user has capacity - use last weight + adaptive multiplier
    finalWeight = lastPerformance.weight * adaptiveMultiplier * readinessMultiplier;
    reasoning = reasoning + ` (pushing from last ${lastPerformance.weight} lbs)`;
    console.log(` HST Fix: Periodization stalled at ${Math.round(periodizationWeight)} lbs, pushing from last performance (RPE trend: ${effectiveRPE.toFixed(1)})`);
  }
  
  return {
    suggestedWeight: roundToNearestPlate(finalWeight),
    suggestedReps: targetReps,
    confidence: 'high',
    reasoning: reasoning || 'Following periodization with adaptive feedback',
    dataPoints: pastPerformances.length,
    lastPerformance: {
      weight: lastPerformance.weight,
      reps: lastPerformance.reps,
      rpe: lastPerformance.rpe,
      date: lastPerformance.date,
    },
  };
}

// REMOVED: getAccessoryRecommendation (deprecated)
// All callers should use getHybridRecommendation with periodizationWeight=0 for no-1RM cases

interface PerformanceData {
  weight: number;
  reps: number;
  rpe: number;
  date: string;
  volume: number; // weight × reps
}

/**
 * Calculate RPE consistency score (0-1)
 * Higher score = more consistent RPE across sessions
 */
function calculateRPEConsistency(performances: PerformanceData[]): number {
  if (performances.length < 2) return 1.0; // Single session = perfectly consistent
  
  const rpes = performances.map(p => p.rpe);
  const avgRPE = rpes.reduce((sum, rpe) => sum + rpe, 0) / rpes.length;
  
  // Calculate standard deviation
  const variance = rpes.reduce((sum, rpe) => sum + Math.pow(rpe - avgRPE, 2), 0) / rpes.length;
  const stdDev = Math.sqrt(variance);
  
  // Convert to consistency score (0-1)
  // stdDev of 0 = 1.0 (perfect), stdDev of 2+ = 0 (very inconsistent)
  const consistency = Math.max(0, 1 - (stdDev / 2));
  
  return consistency;
}

/**
 * Find past performances of a specific exercise
 */
function findPastPerformances(
  exerciseName: string,
  sessions: SessionLog[],
  limit: number = 3
): PerformanceData[] {
  const performances: PerformanceData[] = [];
  const canonicalKey = getCanonical1RMKey(exerciseName);
  const normalizedName = normalizeExerciseName(exerciseName);

  // Sort sessions by date (most recent first)
  const sortedSessions = [...sessions].sort((a, b) =>
    new Date(b.date).getTime() - new Date(a.date).getTime()
  );

  for (const session of sortedSessions) {
    if (performances.length >= limit) break;
    if (!('exercises' in session)) continue;

    for (const exercise of session.exercises) {
      // Match by canonical key (aliases like "RDL" = "Romanian Deadlift" = "Stiff Leg Deadlift")
      // Fall back to normalized name for exercises not in the canonical map
      const exerciseCanonical = getCanonical1RMKey(exercise.exerciseName);
      const matched = exerciseCanonical === canonicalKey ||
        normalizeExerciseName(exercise.exerciseName) === normalizedName;

      if (matched) {
        // Extract best set from this session
        const bestSet = findBestSet(exercise);
        if (bestSet) {
          performances.push({
            weight: bestSet.weight,
            reps: bestSet.reps,
            rpe: bestSet.rpe,
            date: typeof session.date === 'string' ? session.date : session.date.toISOString(),
            volume: computeSetVolume(bestSet, exercise.exerciseName),
          });
          break; // Only take one performance per session
        }
      }
    }
  }
  
  return performances;
}

/**
 * Find the best set from an exercise (highest volume with acceptable RPE)
 */
function findBestSet(exercise: any): { weight: number; reps: number; rpe: number } | null {
  let bestSet: { weight: number; reps: number; rpe: number } | null = null;
  let bestVolume = 0;
  
  // Check regular sets
  if (exercise.sets && Array.isArray(exercise.sets)) {
    for (const set of exercise.sets) {
      // Don't require RPE - use default of 7.5 if not logged
      if (set.weight > 0 && set.reps > 0) {
        const volume = computeSetVolume(set, exercise.exerciseName);
        if (volume > bestVolume) {
          bestVolume = volume;
          bestSet = { weight: set.weight, reps: set.reps, rpe: set.rpe ?? 7.5 };
        }
      }
    }
  }
  
  // Check superset rounds
  if (exercise.supersetRounds && Array.isArray(exercise.supersetRounds)) {
    for (const round of exercise.supersetRounds) {
      if (round.exercises && Array.isArray(round.exercises)) {
        for (const ex of round.exercises) {
          // Match by name
          if (normalizeExerciseName(ex.name) === normalizeExerciseName(exercise.exerciseName)) {
            // Don't require RPE - use default of 7.5 if not logged
            if (ex.weight > 0 && ex.reps > 0) {
              const volume = computeSetVolume(ex, ex.name || exercise.exerciseName);
              if (volume > bestVolume) {
                bestVolume = volume;
                bestSet = { weight: ex.weight, reps: ex.reps, rpe: ex.rpe ?? 7.5 };
              }
            }
          }
        }
      }
    }
  }
  
  return bestSet;
}

/**
 * Calculate progression recommendation based on past performance
 */
function calculateProgression(
  lastPerformance: PerformanceData,
  allPerformances: PerformanceData[],
  targetReps: number
): Omit<AccessoryRecommendation, 'lastPerformance'> {
  const { weight: lastWeight, reps: lastReps, rpe: lastRPE } = lastPerformance;
  
  // RPE-based progression logic
  if (lastRPE <= 6) {
    // Too easy - significant increase
    const increase = lastWeight * 0.10; // 10% increase
    return {
      suggestedWeight: roundToNearestPlate(lastWeight + increase),
      suggestedReps: targetReps,
      confidence: 'high',
      reasoning: `Last session RPE ${lastRPE} was too easy - increasing 10%`,
    };
  } else if (lastRPE <= 7) {
    // Moderate - standard progression
    const increase = lastWeight * 0.05; // 5% increase
    return {
      suggestedWeight: roundToNearestPlate(lastWeight + increase),
      suggestedReps: targetReps,
      confidence: 'high',
      reasoning: `Last session RPE ${lastRPE} - standard 5% progression`,
    };
  } else if (lastRPE === 8) {
    // Target RPE - maintain or small increase
    if (lastReps >= targetReps) {
      // Hit target reps at RPE 8 - small weight increase
      const increase = lastWeight * 0.025; // 2.5% increase
      return {
        suggestedWeight: roundToNearestPlate(lastWeight + increase),
        suggestedReps: targetReps,
        confidence: 'high',
        reasoning: `Hit target reps at RPE 8 - small progression`,
      };
    } else {
      // Didn't hit target reps - maintain weight, push for reps
      return {
        suggestedWeight: roundToNearestPlate(lastWeight),
        suggestedReps: targetReps,
        confidence: 'high',
        reasoning: `RPE 8 but missed reps - maintain weight, push for ${targetReps} reps`,
      };
    }
  } else if (lastRPE === 9) {
    // Hard but manageable - maintain weight
    return {
      suggestedWeight: roundToNearestPlate(lastWeight),
      suggestedReps: targetReps,
      confidence: 'high',
      reasoning: `Last session RPE ${lastRPE} - maintain weight and focus on form`,
    };
  } else {
    // RPE 10 - too hard, reduce weight
    const decrease = lastWeight * 0.05; // 5% decrease
    return {
      suggestedWeight: roundToNearestPlate(lastWeight - decrease),
      suggestedReps: targetReps,
      confidence: 'medium',
      reasoning: `Last session RPE 10 was too hard - reducing 5%`,
    };
  }
}

/**
 * Estimate a conservative starting weight for an exercise with no history
 */
function estimateStartingWeight(exerciseName: string): number {
  const name = exerciseName.toLowerCase();
  
  // Compound movements (higher weight)
  if (name.includes('row') || name.includes('press') && !name.includes('leg')) {
    return 65; // Barbell/dumbbell rows, overhead press variations
  }
  
  // Isolation upper body (moderate weight)
  if (name.includes('curl') || name.includes('extension') || name.includes('raise')) {
    return 35; // Bicep curls, tricep extensions, lateral raises
  }
  
  // Smaller isolation (lighter weight)
  if (name.includes('fly') || name.includes('flye') || name.includes('face pull')) {
    return 20;
  }
  
  // Shrugs and traps (heavier)
  if (name.includes('shrug')) {
    return 70;
  }
  
  // Bodyweight exercises
  if (name.includes('pull-up') || name.includes('push-up') || name.includes('dip')) {
    return 0; // Bodyweight
  }
  
  // Default conservative weight
  return 45;
}

/**
 * Normalize exercise name for comparison
 */
function normalizeExerciseName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special chars
    .replace(/\s+/g, ' ') // Normalize spaces
    .trim();
}

/**
 * Get adaptive recommendations for all exercises in a workout
 */
export async function getWorkoutRecommendations(
  exercises: Array<{ name: string; targetReps: number; baselineWeight?: number }>
): Promise<Map<string, AccessoryRecommendation>> {
  const recommendations = new Map<string, AccessoryRecommendation>();

  for (const exercise of exercises) {
    // Use getHybridRecommendation with periodizationWeight = baselineWeight (or 0)
    const rec = await getHybridRecommendation(
      exercise.name,
      exercise.targetReps,
      exercise.baselineWeight || 0,
      1.0 // Default readiness multiplier
    );
    recommendations.set(exercise.name, rec);
  }

  return recommendations;
}

 
